import { inject, Injectable, Injector } from '@angular/core';
import { FikoYTIstekKok } from '../../../../modules/fikoyt-istek-kok/fikoyt.istek.makinesi.service';

export type KartBildirimTipi = 'kart_olusturuldu' | 'kart_guncellendi' | 'alt_kart_eklendi' | 'kart_silindi' | 'bahsedildi';

@Injectable({ providedIn: 'root' })
export class KartBildirimService {
  private injector = inject(Injector);

  private sonBildirimler = new Map<string, number>();
  private readonly RATE_LIMIT_MS = 5 * 60 * 1000;

  async bildirimTetikle(
    tip: KartBildirimTipi,
    kartId: string,
    kartSahipId: string,
    kartBaslik?: string,
  ): Promise<void> {
    const { FikoYTService } = await import('../../../../fikoyt.service');
    const fikoyt = this.injector.get(FikoYTService);
    const gondericId = fikoyt.kimlik.aktifKullaniciJson()?._id;
    const gondericIsim = fikoyt.kimlik.aktifKullaniciJson()?.isim || '';
    if (!gondericId || !kartSahipId) return;
    if (gondericId === kartSahipId) return;

    const anahtar = `${tip}:${kartId}:${kartSahipId}`;
    const simdi = Date.now();
    const son = this.sonBildirimler.get(anahtar);
    if (son && simdi - son < this.RATE_LIMIT_MS) return;

    this.sonBildirimler.set(anahtar, simdi);

    const mesajlar: Record<KartBildirimTipi, string> = {
      kart_olusturuldu: 'yeni bir kart oluşturdu',
      kart_guncellendi: 'kartınızı güncelledi',
      alt_kart_eklendi: 'kartınıza yorum ekledi',
      kart_silindi: 'kartınızı sildi',
      bahsedildi: 'sizi bir kartta bahsetti',
    };

    await this.bildirimKaydet({
      aliciId: kartSahipId, gondericId, gondericIsim, olay: tip,
      kartId, kartBaslik: kartBaslik ?? '', mesaj: `${gondericIsim} ${mesajlar[tip]}`,
    });

    this.islemLoguKaydet(tip, kartId, { kartBaslik, aliciId: kartSahipId });

    try {
      const { TetiklemeService } = await import('@/src/app/servisler/tetikleme.service');
      const tetiklemeSvc = this.injector.get(TetiklemeService);
      tetiklemeSvc.tetiklemeleriKontrolEt(tip, '', { _id: kartId, baslik: kartBaslik, sahipId: kartSahipId, olusturanId: gondericId });
    } catch {}
  }

  async mentionBildirimGonder(
    kartId: string,
    kartBaslik: string,
    icerikHtmlleri: string[],
  ): Promise<void> {
    const { FikoYTService } = await import('../../../../fikoyt.service');
    const fikoyt = this.injector.get(FikoYTService);
    const gondericId = fikoyt.kimlik.aktifKullaniciJson()?._id;
    const gondericIsim = fikoyt.kimlik.aktifKullaniciJson()?.isim || '';
    if (!gondericId) return;

    const tumIdler = new Set<string>();
    for (const html of icerikHtmlleri) {
      const regex = /data-user-id="([^"]+)"/g;
      let match;
      while ((match = regex.exec(html)) !== null) tumIdler.add(match[1]);
    }

    for (const aliciId of tumIdler) {
      if (aliciId === gondericId) continue;
      const anahtar = `bahsedildi:${kartId}:${aliciId}`;
      const simdi = Date.now();
      const son = this.sonBildirimler.get(anahtar);
      if (son && simdi - son < this.RATE_LIMIT_MS) continue;
      this.sonBildirimler.set(anahtar, simdi);

      await this.bildirimKaydet({
        aliciId, gondericId, gondericIsim, olay: 'bahsedildi',
        kartId, kartBaslik,
        mesaj: `${gondericIsim} sizi bir kartta bahsetti`,
      });
    }
  }

  async islemLoguKaydet(islem: string, kartId: string, detay?: any): Promise<void> {
    try {
      const { FikoYTService } = await import('../../../../fikoyt.service');
      const fikoyt = this.injector.get(FikoYTService);
      const kullanici = fikoyt.kimlik.aktifKullaniciJson();
      const log = {
        vt: 'FikoYT.sistem.islem-loglari',
        islem,
        kartId,
        kullaniciId: kullanici?._id || '',
        kullaniciIsim: (kullanici?.isim || '') + ' ' + (kullanici?.soyIsim || ''),
        detay: detay || {},
        tarih: new Date().toISOString(),
        tarayici: navigator.userAgent,
      };
      const istek = new FikoYTIstekKok({ metod: 'POST', komut: 'FikoYT.sistem.DB.hareketler.ekle' });
      const fd = new FormData();
      fd.append('req_body', JSON.stringify(log));
      istek.parametreler = fd;
      await istek.isteSync();
    } catch {}
  }

  private async bildirimKaydet(veri: {
    aliciId: string; gondericId: string; gondericIsim: string; olay: KartBildirimTipi;
    kartId: string; kartBaslik: string; mesaj: string;
  }): Promise<void> {
    const bildirim: any = {
      vt: 'FikoYT.sistem.bildirimler',
      ...veri,
      okudu: false,
      tarih: new Date().toISOString(),
      oncelik: 'normal',
    };
    try {
      const istek = new FikoYTIstekKok({ metod: 'POST', komut: 'FikoYT.sistem.DB.hareketler.ekle' });
      const fd = new FormData();
      fd.append('req_body', JSON.stringify(bildirim));
      istek.parametreler = fd;
      await istek.isteSync();

      const { FikoYTService } = await import('../../../../fikoyt.service');
      const fikoYT = this.injector.get(FikoYTService);
      fikoYT.io?.emit?.('FikoYT.sistem.io.bildirim', bildirim);
    } catch { }
  }
}
